/**
 * MyReadingListScreen
 * 
 * Shows all books in user's reading list (favorited books)
 * Uses the same template as CategoryScreen
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme, BookCard } from '../../components/ui';
import { BookService } from '../../services/bookService';
import { FireBook } from '../../models/FirestoreModels';
import { auth } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import { getShortTitle, getShortDescription } from '../../utils/bookHelpers';

const MyReadingListScreen: React.FC = () => {
  const navigation = useNavigation();
  const [books, setBooks] = useState<FireBook[]>([]);
  const [loading, setLoading] = useState(true);

  // Reload books whenever the screen comes into focus
  useFocusEffect(
    useCallback(() => {
      loadUserBooks();
    }, [])
  );

  const loadUserBooks = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      // Use PrivateProfileService to get profile
      const result = await PrivateProfileService.getProfile(user.uid);
      
      if (!result.success || !result.data) {
        setLoading(false);
        return;
      }

      const booksProgress = result.data.booksProgress || {};

      // Filter for ONLY favorite books and get their IDs with timestamps
      const favoriteBooks = Object.entries(booksProgress)
        .filter(([_, progress]) => progress.isFavorite === true)
        .map(([bookId, progress]) => ({
          bookId,
          favoriteAddedAt: progress.favoriteAddedAt || 0,
        }));

      if (favoriteBooks.length === 0) {
        setLoading(false);
        return;
      }

      // Sort by newest first (highest favoriteAddedAt timestamp)
      favoriteBooks.sort((a, b) => b.favoriteAddedAt - a.favoriteAddedAt);

      // Use BookService to fetch book details
      const bookPromises = favoriteBooks.map(async ({ bookId }) => {
        try {
          const bookResult = await BookService.getBookDetail(bookId, 'en');
          if (bookResult.success && bookResult.data) {
            return bookResult.data.book;
          }
          return null;
        } catch (error) {
          console.error(`[MyReadingListScreen] Error fetching book ${bookId}:`, error);
          return null;
        }
      });

      const fetchedBooks = await Promise.all(bookPromises);
      const validBooks = fetchedBooks.filter((book) => book !== null) as FireBook[];

      setBooks(validBooks);
    } catch (error) {
      console.error('[MyReadingListScreen] Error loading user books:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookPress = (book: FireBook) => {
    const bookId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
    navigation.navigate('BookDetail' as never, { 
      bookId, 
      languageCode: book.languageCode || 'en'
    } as never);
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
        <Text style={styles.headerTitle}>My Reading List</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {books.length > 0 ? (
          <View style={styles.booksList}>
            {books.map((book) => {
              const shortTitle = getShortTitle(book.title);
              const description = getShortDescription(book.introduction, 100);
              
              return (
                <BookCard
                  key={book.id}
                  title={shortTitle}
                  author={book.author}
                  coverUrl={book.coverImageUrl || book.coverImageFullUrl}
                  insightsCount={book.insightIds?.length || book.statistics?.insights}
                  mentorNote={description}
                  variant="compact"
                  onPress={() => handleBookPress(book)}
                  style={styles.bookCard}
                />
              );
            })}
          </View>
        ) : (
          <View style={styles.emptyState}>
            <Ionicons name="book-outline" size={48} color={Theme.colors.textSecondary} />
            <Text style={styles.emptyTitle}>Your Reading List is Empty</Text>
            <Text style={styles.emptyDescription}>
              Start adding books to your reading list to see them here.
            </Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Theme.spacing.xl,
    paddingTop: Theme.spacing.xl,
    paddingBottom: Theme.spacing.xxxl,
  },
  booksList: {
    gap: Theme.spacing.md,
  },
  bookCard: {
    marginBottom: Theme.spacing.sm,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Theme.spacing.xxxxl,
    paddingHorizontal: Theme.spacing.xl,
  },
  emptyTitle: {
    fontSize: 20,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    marginTop: Theme.spacing.lg,
    marginBottom: Theme.spacing.sm,
  },
  emptyDescription: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
  },
});

export default MyReadingListScreen;

